import { ICONS, type IconId } from './index';
import { INK, LINE, OUTLINE } from './kit';
import { parseRich } from './rich';

const paths = new Map<IconId, Path2D[]>();

function pathsFor(id: IconId): Path2D[] {
  let p = paths.get(id);
  if (!p) {
    p = ICONS[id].map((s) => new Path2D(s.d));
    paths.set(id, p);
  }
  return p;
}

/** Draws an icon with its top-left corner at (x, y), `size` pixels square. */
export function drawIcon(
  ctx: CanvasRenderingContext2D,
  id: IconId,
  x: number,
  y: number,
  size: number,
): void {
  const shapes = ICONS[id];
  const ps = pathsFor(id);
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 32, size / 32);
  ctx.lineJoin = 'round';
  shapes.forEach((s, i) => {
    const p = ps[i] as Path2D;
    ctx.globalAlpha = s.opacity ?? 1;
    if (s.fill) {
      ctx.fillStyle = s.fill;
      ctx.fill(p);
    }
    const stroke = s.stroke === false ? null : (s.stroke ?? INK);
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = s.w ?? (s.fill ? OUTLINE : LINE);
      ctx.lineCap = s.cap ?? 'round';
      ctx.stroke(p);
    }
  });
  ctx.restore();
}

/** Icons in a line of text are this much bigger than the font size, and get this much room. */
const ICON_SCALE = 1.05;
const ICON_PAD = 0.12;

/** How wide a line of text with `:icon:` tokens draws in the current font. */
export function richWidth(ctx: CanvasRenderingContext2D, text: string, size: number): number {
  let w = 0;
  for (const p of parseRich(text))
    w += typeof p === 'string' ? ctx.measureText(p).width : size * (ICON_SCALE + ICON_PAD * 2);
  return w;
}

/**
 * Draws a line of text with `:icon:` tokens in the current font and fill. `y` is the top of
 * the line, `size` the font size in pixels; honors ctx.textAlign (left, center or right).
 */
export function fillRich(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  size: number,
): void {
  const parts = parseRich(text);
  if (parts.length === 1 && typeof parts[0] === 'string') {
    const baseline = ctx.textBaseline;
    ctx.textBaseline = 'top';
    ctx.fillText(parts[0], x, y);
    ctx.textBaseline = baseline;
    return;
  }
  const total = richWidth(ctx, text, size);
  const align = ctx.textAlign;
  let cx = align === 'center' ? x - total / 2 : align === 'right' || align === 'end' ? x - total : x;
  const baseline = ctx.textBaseline;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  const icon = size * ICON_SCALE;
  for (const p of parts) {
    if (typeof p === 'string') {
      ctx.fillText(p, cx, y);
      cx += ctx.measureText(p).width;
    } else {
      cx += size * ICON_PAD;
      drawIcon(ctx, p.icon, cx, y + (size - icon) / 2, icon);
      cx += icon + size * ICON_PAD;
    }
  }
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
}
